/**
 * IndexedDB implementation of OfflineStorage (via Dexie) — used by the PWA
 * and the web app. On the store apps the encrypted SQLite adapter is used
 * instead (ADR-0021: browser storage is the weaker fallback).
 *
 * Encryption at rest: every sensitive value is sealed with the per-device
 * AES-GCM key (lib/security/device-key.ts) before it reaches IndexedDB and
 * opened on the way out, so callers see plain records. What stays plaintext
 * is listed in sealing.ts. Rows are sealed before a Dexie transaction starts:
 * awaiting WebCrypto inside one would end it early.
 */
import type { OutboxItem } from '@sss/sync';
import type { CarriedFinding, PackCustomer, PackSite, PackTemplateVersion, SyncPack } from '../api/types';
import { RecordCipher } from '../security/cipher';
import { assertWebCrypto, deleteDeviceKey, loadOrCreateDeviceKey } from '../security/device-key';
import { ALL_TABLES, DeviceDb, DEVICE_DB_NAME, PACK_TABLES, type TableName } from './db';
import {
  openAttachment,
  openCustomer,
  openDraft,
  openFinding,
  openMeta,
  openOutbox,
  openSite,
  sealAttachment,
  sealCustomer,
  sealDraft,
  sealFinding,
  sealMeta,
  sealOutbox,
  sealSite,
} from './sealing';
import { siteMatches, type AttachmentRecord, type DraftRecord, type OfflineStorage } from './types';

export class IndexedDbStorage implements OfflineStorage {
  readonly kind = 'indexeddb' as const;
  private dexie: DeviceDb | null = null;
  private cipherRef: RecordCipher | null = null;

  constructor(readonly dbName = DEVICE_DB_NAME) {}

  /** Fails closed with EncryptionUnavailableError when WebCrypto is missing. */
  async init(): Promise<void> {
    if (this.dexie) return;
    assertWebCrypto();
    const cipher = new RecordCipher(await loadOrCreateDeviceKey(this.dbName));
    const db = new DeviceDb(this.dbName, cipher);
    await db.open();
    this.cipherRef = cipher;
    this.dexie = db;
  }

  private get db(): DeviceDb {
    if (!this.dexie) throw new Error('OfflineStorage not initialised — call init()');
    return this.dexie;
  }

  private get cipher(): RecordCipher {
    if (!this.cipherRef) throw new Error('OfflineStorage not initialised — call init()');
    return this.cipherRef;
  }

  // ---- outbox (OutboxStore) -------------------------------------------------
  async all(): Promise<OutboxItem[]> {
    const rows = await this.db.outbox.orderBy('seq').toArray();
    return Promise.all(rows.map((r) => openOutbox(this.cipher, r)));
  }
  async put(item: OutboxItem): Promise<void> {
    await this.db.outbox.put(await sealOutbox(this.cipher, item));
  }
  async remove(id: string): Promise<void> {
    await this.db.outbox.delete(id);
  }

  // ---- offline pack -----------------------------------------------------------
  async replacePack(pack: SyncPack): Promise<void> {
    const db = this.db;
    const c = this.cipher;
    const [sites, customers, findings, serverTime, downloadedAt] = await Promise.all([
      Promise.all(pack.sites.map((s) => sealSite(c, s))),
      Promise.all(pack.customers.map((x) => sealCustomer(c, x))),
      Promise.all((pack.carriedFindings ?? []).map((f) => sealFinding(c, f))),
      sealMeta(c, 'pack.serverTime', pack.serverTime),
      sealMeta(c, 'pack.downloadedAt', Date.now()),
    ]);
    await db.transaction('rw', [...PACK_TABLES, 'kv', 'drafts'], async () => {
      // Template versions used by drafts on this device must survive a pack refresh:
      // a survey is pinned to the version it started on (ADR-0009).
      const pinned = new Set((await db.drafts.toArray()).map((d) => d.templateVersionId));
      const fresh = new Set(pack.templates.map((t) => t.versionId));
      const keep = (await db.templates.toArray()).filter((t) => pinned.has(t.versionId) && !fresh.has(t.versionId));
      await Promise.all(PACK_TABLES.map((t) => db.table(t).clear()));
      await db.sites.bulkPut(sites);
      await db.customers.bulkPut(customers);
      await db.templates.bulkPut([...pack.templates, ...keep]);
      await db.findings.bulkPut(findings);
      await db.kv.put(serverTime, 'pack.serverTime');
      await db.kv.put(downloadedAt, 'pack.downloadedAt');
    });
  }
  async getSite(id: string): Promise<PackSite | undefined> {
    const r = await this.db.sites.get(id);
    return r ? openSite(this.cipher, r) : undefined;
  }
  async listSites(): Promise<PackSite[]> {
    const rows = await this.db.sites.toArray();
    return Promise.all(rows.map((r) => openSite(this.cipher, r)));
  }
  async searchSites(query: string, limit = 50): Promise<PackSite[]> {
    const all = await this.listSites();
    return all
      .filter((s) => siteMatches(s, query))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, limit);
  }
  async putSites(sites: PackSite[]) {
    const rows = await Promise.all(sites.map((s) => sealSite(this.cipher, s)));
    await this.db.sites.bulkPut(rows);
  }
  async getCustomer(id: string): Promise<PackCustomer | undefined> {
    const r = await this.db.customers.get(id);
    return r ? openCustomer(this.cipher, r) : undefined;
  }
  async putCustomers(customers: PackCustomer[]) {
    const rows = await Promise.all(customers.map((x) => sealCustomer(this.cipher, x)));
    await this.db.customers.bulkPut(rows);
  }
  getTemplateVersion(versionId: string): Promise<PackTemplateVersion | undefined> {
    return this.db.templates.get(versionId);
  }
  async putTemplateVersion(t: PackTemplateVersion) {
    await this.db.templates.put(t);
  }
  async findingsForSite(siteId: string): Promise<CarriedFinding[]> {
    const rows = await this.db.findings.where('siteId').equals(siteId).toArray();
    return Promise.all(rows.map((r) => openFinding(this.cipher, r)));
  }

  // ---- drafts -------------------------------------------------------------------
  async putDraft(d: DraftRecord) {
    await this.db.drafts.put(await sealDraft(this.cipher, d));
  }
  async getDraft(id: string): Promise<DraftRecord | undefined> {
    const r = await this.db.drafts.get(id);
    return r ? openDraft(this.cipher, r) : undefined;
  }
  async listDrafts(): Promise<DraftRecord[]> {
    const rows = await this.db.drafts.orderBy('updatedAt').reverse().toArray();
    return Promise.all(rows.map((r) => openDraft(this.cipher, r)));
  }
  async deleteDraft(id: string) {
    await this.db.drafts.delete(id);
  }

  // ---- attachments ------------------------------------------------------------
  async putAttachment(a: AttachmentRecord) {
    await this.db.attachments.put(await sealAttachment(this.cipher, a));
  }
  async getAttachment(id: string): Promise<AttachmentRecord | undefined> {
    const r = await this.db.attachments.get(id);
    return r ? openAttachment(this.cipher, r) : undefined;
  }
  async listAttachments(surveyId: string): Promise<AttachmentRecord[]> {
    const rows = await this.db.attachments.where('surveyId').equals(surveyId).toArray();
    return Promise.all(rows.map((r) => openAttachment(this.cipher, r)));
  }
  async deleteAttachment(id: string) {
    await this.db.attachments.delete(id);
  }

  // ---- kv -------------------------------------------------------------------------
  async getMeta<T>(key: string): Promise<T | undefined> {
    const env = await this.db.kv.get(key);
    return env ? openMeta<T>(this.cipher, key, env) : undefined;
  }
  async setMeta(key: string, value: unknown): Promise<void> {
    await this.db.kv.put(await sealMeta(this.cipher, key, value), key);
  }

  /**
   * Remove everything and destroy the device key (crypto-shredding): even if
   * the browser keeps deleted records on disk for a while, they can no longer
   * be decrypted. A fresh key is created so the app can keep working after
   * the user signs in again.
   */
  async wipe(): Promise<void> {
    const db = this.db;
    try {
      await db.transaction('rw', [...ALL_TABLES], async () => {
        await Promise.all(ALL_TABLES.map((t) => db.table(t).clear()));
      });
    } finally {
      this.cipherRef = null;
      await deleteDeviceKey(this.dbName);
      this.cipherRef = new RecordCipher(await loadOrCreateDeviceKey(this.dbName));
    }
  }

  /** For tests. */
  count(table: TableName): Promise<number> {
    return this.db.table(table).count();
  }

  close(): void {
    this.dexie?.close();
    this.dexie = null;
    this.cipherRef = null;
  }
}
